import { useState } from "react";
import type { PlanOperationDto } from "../api/client";
import { formatQuantity, formatShortDate, formatTimeRange } from "../format";
import { useSession } from "../session";
import { PersonChips } from "./EventList";

export const typeLabels: Record<string, string> = {
  "calendar.create_event": "Neuer Termin",
  "calendar.update_event": "Termin ändern",
  "calendar.cancel_event": "Termin absagen",
  "calendar.cancel_occurrence": "Einzeltermin absagen",
  "shopping.add_item": "Einkauf hinzufügen",
  "shopping.set_item_status": "Einkaufsstatus ändern",
  "shopping.update_item": "Einkaufsartikel ändern",
};

/**
 * Eine vorgeschlagene Aktion – gelesen oder direkt bearbeitbar (Prüfseite und Sprach-Fenster). Annahmen des Vorschlags
 * stehen als „bitte prüfen“ dabei. Bearbeitungen werden als vollständiger Eintrag gemeldet.
 */
export function OperationCard({ op, editable, selected, onSelect, onEdit }: {
  op: PlanOperationDto; editable: boolean; selected: boolean; onSelect: (v: boolean) => void; onEdit: (patch: Record<string, unknown>) => void;
}) {
  const { me } = useSession();
  const payload = op.payload as Record<string, unknown>;
  const [local, setLocal] = useState<Record<string, unknown>>(payload);
  const set = (k: string, v: unknown) => {
    setLocal({ ...local, [k]: v });
    onEdit({ [k]: v });
  };
  const blocking = op.issues.filter((i) => i.severity === "blocking");

  return (
    <li className={`op${blocking.length ? " blocked" : ""}${selected ? "" : " deselected"}`}>
      <div className="row between">
        <strong>{typeLabels[op.type] ?? op.type}</strong>
        {editable && <label className="check small"><input type="checkbox" checked={selected} onChange={(e) => onSelect(e.target.checked)} /> übernehmen</label>}
      </div>
      {op.type === "calendar.create_event" && (
        editable ? (
          <div className="op-fields">
            <label>Titel<input value={String(local.title ?? "")} onChange={(e) => set("title", e.target.value)} /></label>
            <label className="check"><input type="checkbox" checked={Boolean(local.allDay)} onChange={(e) => set("allDay", e.target.checked)} /> ganztägig</label>
            <label>Beginn<input type={local.allDay ? "date" : "datetime-local"} value={String(local.start ?? "")} onChange={(e) => set("start", e.target.value)} /></label>
            <label>Ende<input type={local.allDay ? "date" : "datetime-local"} value={String(local.end ?? "")} onChange={(e) => set("end", e.target.value || null)} /></label>
            <label>Ort<input value={String(local.location ?? "")} onChange={(e) => set("location", e.target.value || null)} /></label>
            <PersonPicker value={(local.participants as { personId: string }[] | null) ?? []} onChange={(v) => set("participants", v)} />
            {local.recurrence ? <p className="small">Wiederholung: {String(local.recurrence)}</p> : null}
          </div>
        ) : (
          <p>
            {String(payload.title)} · {formatShortDate(String(payload.start))} · {formatTimeRange(String(payload.start), payload.end as string | null, Boolean(payload.allDay))}
            {payload.location ? <><br />Ort: {String(payload.location)}</> : null}
            <PersonChips ids={((payload.participants as { personId: string }[] | null) ?? []).map((x) => x.personId)} persons={me.persons} />
          </p>
        )
      )}
      {op.type === "shopping.add_item" && (
        editable ? (
          <div className="op-fields row">
            <label>Artikel<input value={String(local.name ?? "")} onChange={(e) => set("name", e.target.value)} /></label>
            <label>Menge<input value={local.quantity == null ? "" : String(local.quantity)} inputMode="decimal" onChange={(e) => set("quantity", e.target.value ? Number(e.target.value.replace(",", ".")) : null)} /></label>
            <label>Einheit<input value={String(local.unit ?? "")} onChange={(e) => set("unit", e.target.value || null)} /></label>
          </div>
        ) : (
          <p>{String(payload.name)} {formatQuantity(payload.quantity as number | null, payload.unit as string | null)}</p>
        )
      )}
      {!["calendar.create_event", "shopping.add_item"].includes(op.type) && <pre className="payload">{JSON.stringify(payload, null, 2)}</pre>}
      {op.sourceExcerpt && <p className="muted small">Quelle: „{op.sourceExcerpt}“</p>}
      {op.issues.length > 0 && (
        <ul className="issues">
          {op.issues.map((i, n) => <li key={n} className={`issue-${i.severity}`}>{i.message}</li>)}
        </ul>
      )}
    </li>
  );
}

export function PersonPicker({ value, onChange }: { value: { personId: string }[]; onChange: (v: { personId: string; role: string }[]) => void }) {
  const { me } = useSession();
  const ids = value.map((v) => v.personId);
  return (
    <fieldset className="persons-inline">
      <legend>Personen</legend>
      {me.persons.filter((p) => p.isActive).map((p) => (
        <label key={p.id} className="check">
          <input type="checkbox" checked={ids.includes(p.id)}
            onChange={(e) => onChange((e.target.checked ? [...ids, p.id] : ids.filter((x) => x !== p.id)).map((personId) => ({ personId, role: "attendee" })))} />
          <span className="swatch" style={{ background: p.color }} aria-hidden="true" /> {p.displayName}
        </label>
      ))}
    </fieldset>
  );
}
